import type { LucideIcon } from "lucide-react";

import { cn } from "@/shared/lib/utils";

import { StatCard } from "./StatCard";

/** A single "at a glance" entry — a posture fact rendered as a stat card. */
export interface GlanceItem {
	/** Stable key for React. */
	id: string;
	/** Small-caps label (the row's term in the source "At a glance" table). */
	label: string;
	/** The headline value (the source row's definition, distilled to a phrase). */
	value: string;
	/** Optional supporting line under the value. */
	hint?: string;
	/** Optional lucide icon for the card's corner chip. */
	icon?: LucideIcon;
}

interface AtAGlanceProps {
	items: ReadonlyArray<GlanceItem>;
	/** Column count at the `lg` breakpoint. Defaults to 2 (matches source tables). */
	columns?: 2 | 3 | 4;
	className?: string;
}

// Map the requested column count to an explicit Tailwind class so the responsive
// grid classes are statically present for the JIT compiler (no dynamic class names).
const COLUMN_CLASS: Record<NonNullable<AtAGlanceProps["columns"]>, string> = {
	2: "sm:grid-cols-2",
	3: "sm:grid-cols-2 lg:grid-cols-3",
	4: "sm:grid-cols-2 lg:grid-cols-4",
};

/**
 * The "At a glance" summary grid: the source docs' two-column posture tables,
 * promoted to a row of designed StatCards with icons. This is the page's first
 * scannable payload — the facts a security reviewer wants in five seconds.
 */
export function AtAGlance({ items, columns = 2, className }: AtAGlanceProps) {
	return (
		<div className={cn("grid grid-cols-1 gap-4", COLUMN_CLASS[columns], className)}>
			{items.map((item) => (
				<StatCard key={item.id} label={item.label} value={item.value} hint={item.hint} icon={item.icon} />
			))}
		</div>
	);
}
